import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, FlatList, Image, Image as ImageView, Linking, Modal, Platform, StyleSheet, TouchableOpacity, View } from 'react-native';
import { ButtonTypeDLS, ColorTokens, FontFamilyTokens, FontSizeTokens, IconSizeTokens, IconTokens, SizeTypeTokens, TypographyTypeDLS, UploadFileStatus } from "@voltmoney/schema";
import { Icon } from "../icon/Icon";
import { Space } from "../space/Space";
import { Typography } from "../typography/Typography";
import ThemeProvider from "@voltmoney/theme";
import { isWeb } from "../pages/utils";
import { camera } from "../documentPicker/camera.native";
import { Button } from "../button/Button";
import { isMobileView } from "../stack/utils";
/**
 * Primary UI component for user interaction
 */
const UploadFile = ({ status, uploadAction, deleteAction, errorMessage, setUploadFile, onDeleteFile, message, triggerAction, isFromAddLogo, isEditClicked, isPhotoRemoved, setUploadFileURL, imageUrlFromSource, fileFormatIncorrect, callSimilarCallback, isFromASESDocumentsFlow, viewFileAction, viewUploadedFile, errorHalfCardClosed, showDocUploaded, existingFileURLASES, fileName, documentType, loading, }) => {
    var data = isFromASESDocumentsFlow && Platform.OS === "ios" ? [
        { label: 'Gallery', value: '2' },
        { label: 'File', value: '3' },
    ] : [
        { label: 'Camera', value: '1' },
        { label: 'Gallery', value: '2' },
        { label: 'File', value: '3' },
    ];
    const DropdownButton = useRef(undefined);
    const [visible, setVisible] = useState(false);
    const [_status, setStatus] = useState(status ? status : UploadFileStatus.DEFAULT);
    const [_uploadFile, setUploadFileName] = useState(undefined);
    const [_uploadFileType, setUploadFileType] = useState(undefined);
    const [_documentPickerWeb, setDocumentPickerWeb] = useState(false);
    const [_errorMessage, setErrorMessage] = useState(undefined);
    const [selectedFile, setSelectedFile] = useState(null);
    const [filePreview, setFilePreview] = useState('');
    const hiddenFileInput = React.useRef(null);
    const [hasCancelled, setHasCancelled] = useState(false);
    const [tempFile, setTempFile] = useState(undefined);
    const [displayUrl, setDisplayUrl] = useState("");
    const [uploadDocumentLoading, setUploadDocumentLoading] = useState(false);
    const theme = ThemeProvider.getTheme();
    const colorMapping = theme.colors;
    const labelColorValue = theme.colors[ColorTokens.Primary_100];
    const MAX_ALLOWED_SIZE_MB = 5;
    const styles = StyleSheet.create({
        uploadContainer: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            borderColor: '#D0D6F5',
            borderWidth: 2,
            backgroundColor: '#F3F5FC',
            padding: 12,
            borderStyle: 'dashed',
            borderRadius: 8
        },
        uploadSuccessParentContainer: {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between'
        },
        uploadSuccessLeftContainer: {
            flex: 1,
            maxWidth: 200
        },
        item: {
            paddingHorizontal: 12,
            paddingVertical: 12,
            borderBottomWidth: 1,
            borderBottomColor: colorMapping[ColorTokens.Grey_Milk],
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
        },
        overlay: {
            flex: 1,
            backgroundColor: '#0000007F',
            justifyContent: 'center',
            alignItems: 'center',
        },
        dropdown: {
            backgroundColor: colorMapping[ColorTokens.White],
            shadowColor: colorMapping[ColorTokens.Grey_Smoke],
            borderColor: colorMapping[ColorTokens.Grey_Milk],
            borderWidth: 1,
            shadowOpacity: 0.1,
            shadowRadius: 4,
            height: isFromASESDocumentsFlow && Platform.OS === "ios" ? 100 : 150,
            width: 200
        },
        addPhotoContainer: {
            marginTop: 16,
            paddingHorizontal: 18,
            paddingVertical: 24,
            justifyContent: 'center',
            alignContent: 'center',
            alignItems: 'center',
            width: 96,
            height: 96,
            borderRadius: 4,
            borderStyle: 'dashed',
            borderWidth: 1,
            borderColor: '#98A2B3'
        },
        addPhotoInsideContainer: {
            justifyContent: 'center',
            alignContent: 'center',
            alignItems: 'center',
        },
        marginTop2: {
            marginTop: 2
        },
    });
    useEffect(() => {
        if (isPhotoRemoved) {
            setStatus(UploadFileStatus.DEFAULT);
            setUploadFileURL(undefined, undefined);
        }
    }, [isPhotoRemoved]);
    useEffect(() => {
        if (fileFormatIncorrect) {
            setStatus(UploadFileStatus.DEFAULT);
        }
    }, [fileFormatIncorrect]);
    useEffect(() => {
        if (isEditClicked) {
            handleDocumentSelection();
        }
    }, [isEditClicked]);
    const handleDocumentSelection = useCallback(async () => {
        setUploadDocumentLoading(true);
        try {
            if (isWeb()) {
                setUploadDocumentLoading(false);
                hiddenFileInput.current.click();
            }
            else {
                setUploadDocumentLoading(false);
                toggleDropdown();
            }
        }
        catch (err) {
            console.warn(err);
        }
    }, []);
    const onPressUpload = (e) => {
        if (e && isWeb()) {
            let file = e.target.files[0];
            setTempFile(file);
            if (file) {
                setSelectedFile(file);
                if (!isFromASESDocumentsFlow && file.size > MAX_ALLOWED_SIZE_MB * 1024 * 1024) {
                    setErrorMessage(errorMessage);
                    setStatus(UploadFileStatus.FAILED);
                    return;
                }
                const fileURL = URL.createObjectURL(file);
                setFilePreview(fileURL);
            }
            setUploadFileName(file);
        }
        e.target.value = null;
    };
    const onPressDelete = () => {
        if (loading)
            return;
        deleteAction &&
            triggerAction(deleteAction);
        onDeleteFile &&
            onDeleteFile();
        setUploadFileName(undefined);
        setStatus(UploadFileStatus.DEFAULT);
    };
    useEffect(() => {
        if (imageUrlFromSource) {
            setStatus(UploadFileStatus.SUCCESS);
            setDisplayUrl(imageUrlFromSource);
        }
        else if (showDocUploaded) {
            setStatus(UploadFileStatus.SUCCESS);
        }
        else {
            setStatus(UploadFileStatus.DEFAULT);
        }
    }, [imageUrlFromSource, showDocUploaded]);
    useEffect(() => {
        if (_uploadFile) {
            setUploadFile &&
                setUploadFile(_uploadFile);
            setUploadFileURL && setUploadFileURL(filePreview, _uploadFile);
            uploadAction &&
                triggerAction(uploadAction);
            setStatus(UploadFileStatus.SUCCESS);
        }
    }, [_uploadFile]);
    useEffect(() => {
        setErrorMessage(errorMessage);
    }, [errorMessage]);
    const uploadFileContainer = () => {
        return isFromAddLogo ? (React.createElement(TouchableOpacity, { onPress: handleDocumentSelection, style: styles.addPhotoContainer },
            React.createElement(View, { style: styles.addPhotoInsideContainer },
                React.createElement(Icon, { name: IconTokens.AddPhoto, size: IconSizeTokens.XXL }),
                React.createElement(View, { style: styles.marginTop2 },
                    React.createElement(Typography, { label: "Add logo", fontWeight: "600", numberOfLines: 1, fontSize: FontSizeTokens.SM, lineHeight: 20, fontFamily: FontFamilyTokens.Inter, color: ColorTokens.Primary_100 }))))) : isFromASESDocumentsFlow ? (React.createElement(React.Fragment, null,
            React.createElement(View, { ref: DropdownButton },
                React.createElement(Button, { typeDLS: ButtonTypeDLS.PRIMARY, label: 'Upload document ', iconName: IconTokens.Upload, iconAlignment: "right", onPress: handleDocumentSelection, loading: uploadDocumentLoading })))) : (React.createElement(React.Fragment, null,
            React.createElement(TouchableOpacity, { ref: DropdownButton, onPress: handleDocumentSelection, style: styles.uploadContainer },
                _status === UploadFileStatus.LOADING
                    ? React.createElement(ActivityIndicator, { animating: true, color: labelColorValue })
                    : React.createElement(Icon, { name: IconTokens.Upload, size: IconSizeTokens.XXL }),
                React.createElement(Space, { size: SizeTypeTokens.MD }),
                React.createElement(Typography, { label: _status === UploadFileStatus.LOADING
                        ? 'Uploading'
                        : 'Upload file', fontSize: FontSizeTokens.SM, fontWeight: '500', lineHeight: 20, fontFamily: FontFamilyTokens.Inter, color: ColorTokens.Primary_100 })),
            _status === UploadFileStatus.FAILED
                &&
                    React.createElement(React.Fragment, null,
                        React.createElement(Space, { size: SizeTypeTokens.MD }),
                        React.createElement(Typography, { label: errorMessage ? errorMessage : 'Upload failed. Please try again.', fontFamily: FontFamilyTokens.Inter, fontWeight: '400', fontSize: FontSizeTokens.XS, lineHeight: 18, color: ColorTokens.System_Error })),
            _status === UploadFileStatus.DEFAULT
                &&
                    React.createElement(React.Fragment, null,
                        React.createElement(Space, { size: SizeTypeTokens.MD }),
                        React.createElement(Typography, { label: message ? message : 'message', fontFamily: FontFamilyTokens.Inter, fontWeight: '400', fontSize: FontSizeTokens.XS, lineHeight: 18, color: ColorTokens.Grey_Charcoal }))));
    };
    const uploadFileSuccess = () => {
        return isFromAddLogo ? (React.createElement(View, null,
            React.createElement(TouchableOpacity, { style: styles.addPhotoContainer },
                React.createElement(ImageView, { style: { width: 96, height: 96 }, source: { uri: filePreview ? filePreview : imageUrlFromSource } })))) : isFromASESDocumentsFlow ? (React.createElement(View, { style: {
                backgroundColor: '#F3F5FC',
                borderRadius: 8,
                borderWidth: 1,
                borderColor: '#F9FAFB',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignContent: 'center',
                alignItems: 'center',
                paddingVertical: 12, paddingHorizontal: 8,
                marginBottom: 8
            } },
            React.createElement(View, { style: {
                    flexDirection: 'row',
                    alignContent: 'center',
                    alignItems: 'center'
                } },
                React.createElement(Image, { source: { uri: "https://volt-images-alpha.s3.ap-south-1.amazonaws.com/preview_pdf.png" }, style: { width: 32, height: 32, marginRight: 12 } }),
                React.createElement(Typography, { label: isMobileView()
                        ? (_uploadFile?.name || _uploadFile?.fileName || fileName).length > 25
                            ? (_uploadFile?.name || _uploadFile?.fileName || fileName).substring(0, 25) + "..."
                            : (_uploadFile?.name || _uploadFile?.fileName || fileName)
                        : (_uploadFile?.name || fileName || _uploadFile?.fileName).length > 43
                            ? (_uploadFile?.name || fileName || _uploadFile?.fileName).substring(0, 43) + "..."
                            : (_uploadFile?.name || fileName || _uploadFile?.fileName), typeDLS: TypographyTypeDLS.B1M, color: ColorTokens.Grey_Night, numberOfLines: 1 })),
            React.createElement(View, { style: { flexDirection: 'row', alignContent: 'center', alignItems: 'center' } },
                showDocUploaded ?
                    (existingFileURLASES && (_uploadFile?.name || _uploadFile?.fileName))
                        ? React.createElement(React.Fragment, null) :
                        React.createElement(TouchableOpacity, { activeOpacity: 1, onPress: () => {
                                isMobileView() ? Linking.openURL(existingFileURLASES).catch((err) => console.error('An error occurred', err)) :
                                    triggerAction({
                                        ...viewUploadedFile,
                                        payload: {
                                            fileUrl: existingFileURLASES,
                                            fileName: fileName,
                                            documentType: documentType
                                        }
                                    });
                            } },
                            React.createElement(Typography, { label: "View", typeDLS: TypographyTypeDLS.B1M, color: ColorTokens.Primary_100 })) : React.createElement(React.Fragment, null),
                React.createElement(Space, { size: SizeTypeTokens.XL }),
                React.createElement(TouchableOpacity, { onPress: () => onPressDelete() },
                    React.createElement(Icon, { name: IconTokens.System_delete }))))) : (React.createElement(React.Fragment, null,
            React.createElement(View, { style: styles.uploadSuccessParentContainer },
                React.createElement(View, { style: styles.uploadSuccessLeftContainer },
                    React.createElement(Typography, { label: (isWeb() || _uploadFileType === 'File')
                            ? `${_uploadFile?.name}`
                            : `${_uploadFile?.fileName}`, fontSize: FontSizeTokens.SM, fontWeight: '400', fontFamily: FontFamilyTokens.Inter, numberOfLines: 2, lineHeight: 20 })),
                React.createElement(TouchableOpacity, { onPress: () => onPressDelete() },
                    React.createElement(Icon, { name: IconTokens.System_delete })))));
    };
    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
    const handleCancel = () => {
        setUploadFileName(undefined);
        setUploadFileType(undefined);
        setStatus(UploadFileStatus.DEFAULT);
    };
    const onItemPress = async (item) => {
        setVisible(false);
        if (Platform.OS === 'web') {
            hiddenFileInput.current.click();
        }
        else {
            sleep(500).then(() => {
                camera(item, handleCancel).then((res) => {
                    if (res.hasOwnProperty('fileSize')) {
                        //@ts-ignore
                        if (!isFromASESDocumentsFlow && res?.fileSize > MAX_ALLOWED_SIZE_MB * 1024 * 1024) {
                            setErrorMessage(errorMessage);
                            setStatus(UploadFileStatus.FAILED);
                            return;
                        }
                    }
                    if (!isFromASESDocumentsFlow && res.hasOwnProperty('size')) {
                        //@ts-ignore
                        if (res?.size > MAX_ALLOWED_SIZE_MB * 1024 * 1024) {
                            setErrorMessage(errorMessage);
                            setStatus(UploadFileStatus.FAILED);
                            return;
                        }
                    }
                    let resultFile = res;
                    //@ts-ignore
                    if (res && res.hasOwnProperty('fileName') && res?.fileName?.includes('rn_image_picker_lib_temp_')) {
                        //@ts-ignore
                        resultFile.fileName = res?.fileName.replace(/^rn_image_picker_lib_temp_/, '');
                    }
                    //@ts-ignore
                    setUploadFileType(item?.label);
                    setUploadFileName(resultFile);
                    setStatus(UploadFileStatus.SUCCESS);
                });
            });
        }
    };
    const renderItem = ({ item, }) => (React.createElement(TouchableOpacity, { style: {
            alignItems: 'center',
            justifyContent: 'center',
        }, onPress: () => onItemPress(item) },
        React.createElement(View, { style: styles.item },
            React.createElement(Typography, { label: item.label, color: ColorTokens.Grey_Night, fontSize: FontSizeTokens.MD, fontWeight: '400', fontFamily: FontFamilyTokens.Inter }))));
    const item = { label: 'File', value: '3' };
    const openFileSystem = () => {
        setVisible(false);
        sleep(500).then(() => {
            setUploadDocumentLoading(false);
            camera(item, handleCancel, true).then((res) => {
                if (res.hasOwnProperty('fileSize')) {
                    //@ts-ignore
                    if (!isFromASESDocumentsFlow && res?.fileSize > MAX_ALLOWED_SIZE_MB * 1024 * 1024) {
                        setErrorMessage(errorMessage);
                        setStatus(UploadFileStatus.FAILED);
                        return;
                    }
                }
                if (!isFromASESDocumentsFlow && res.hasOwnProperty('size')) {
                    //@ts-ignore
                    if (res?.size > MAX_ALLOWED_SIZE_MB * 1024 * 1024) {
                        setErrorMessage(errorMessage);
                        setStatus(UploadFileStatus.FAILED);
                        return;
                    }
                }
                let resultFile = res;
                //@ts-ignore
                if (res && res.hasOwnProperty('fileName') && res?.fileName?.includes('rn_image_picker_lib_temp_')) {
                    //@ts-ignore
                    resultFile.fileName = res?.fileName.replace(/^rn_image_picker_lib_temp_/, '');
                }
                //@ts-ignore
                setUploadFileType(item?.label);
                setUploadFileName(resultFile);
                setStatus(UploadFileStatus.SUCCESS);
            });
        });
    };
    const renderDropdown = () => {
        return (React.createElement(Modal, { visible: visible, transparent: true, animationType: 'slide' },
            React.createElement(TouchableOpacity, { style: styles.overlay, onPress: () => {
                    setStatus(UploadFileStatus.DEFAULT);
                    setVisible(false);
                } },
                React.createElement(View, { style: [styles.dropdown] },
                    React.createElement(FlatList, { data: data, renderItem: renderItem, ItemSeparatorComponent: () => (React.createElement(View, { style: {
                                backgroundColor: 'grey',
                                height: 0.5,
                            } })), keyExtractor: (item, index) => index.toString() })))));
    };
    const openDropdown = () => {
        setVisible(true);
    };
    useEffect(() => {
        if (errorHalfCardClosed) {
            setStatus(UploadFileStatus.DEFAULT);
        }
    }, [errorHalfCardClosed]);
    const toggleDropdown = () => {
        visible ? setVisible(false) : openDropdown();
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(View, null,
            _status === UploadFileStatus.SUCCESS
                ? (uploadFileSuccess()) : (uploadFileContainer()),
            Platform.OS === 'web' && isFromAddLogo &&
                React.createElement(React.Fragment, null,
                    React.createElement("input", { ref: hiddenFileInput, type: "file", accept: ".jpg, .png, .jpeg", onChange: onPressUpload, style: { display: 'none' } })),
            Platform.OS === 'web' &&
                React.createElement(React.Fragment, null,
                    React.createElement("input", { ref: hiddenFileInput, type: "file", accept: isFromASESDocumentsFlow ? ".pdf, .jpeg" : ".pdf, .png, .jpeg", onChange: onPressUpload, style: { display: 'none' } }))),
        visible && renderDropdown()));
};
export default memo(UploadFile);
export { UploadFile };
